/**
 * The app's one zoom, as a piece of component state.
 *
 * Reads the stored value on mount and then listens, so a grid that is already
 * on screen when another surface changes the zoom resizes with it rather than
 * waiting to be remounted. See utils/zoomPreference.js for why there is only
 * one value at all.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  readZoom, writeZoom, nextZoom, clampZoom, ZOOM_EVENT,
} from '../utils/zoomPreference';

export default function useZoom() {
  const [zoom, setZoomState] = useState(readZoom);

  useEffect(() => {
    const onLocal = (e) => setZoomState(clampZoom(e.detail));
    // `storage` covers the other tab on the web build; the custom event covers
    // this one, because localStorage does not notify the tab that wrote it.
    const onStorage = (e) => {
      if (e.key === 'cardchase.zoom') setZoomState(clampZoom(e.newValue));
    };
    window.addEventListener(ZOOM_EVENT, onLocal);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(ZOOM_EVENT, onLocal);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const setZoom = useCallback((value) => {
    setZoomState(writeZoom(typeof value === 'function' ? value(readZoom()) : value));
  }, []);

  const cycleZoom = useCallback(() => {
    setZoomState(writeZoom(nextZoom(readZoom())));
  }, []);

  return { zoom, setZoom, cycleZoom };
}
